/**
 * Cookie consent utilities — POPIA s11 + GDPR Art. 7 + ePrivacy compliant.
 *
 * Stores the visitor's granular consent (necessary / analytics / marketing)
 * in localStorage so we don't re-prompt on every page load, and POSTs the
 * decision to `/v1/me/consent` when the visitor is authenticated so the
 * choice survives across devices + ends up on the user's audit trail.
 *
 * Why localStorage + a server roundtrip:
 *   - localStorage handles the anonymous / pre-signup case (the most
 *     common reason a banner appears).
 *   - Server persistence covers the "consent should follow the user across
 *     devices" expectation that POPIA + GDPR audits look for.
 *
 * Why DNT respect:
 *   - South African POPIA doesn't mandate DNT honour, but the SAGEPS draft
 *     guidance on cookie consent (2023) leans towards "explicit opt-in
 *     overrides DNT only when user has actively consented to non-essential
 *     categories". The conservative read is "DNT == decline all non-
 *     essential by default; user can still opt back in via the banner".
 *   - GDPR Art. 7 + the EDPB cookie guidance treat DNT the same way.
 *
 * Versioning:
 *   - `POLICY_VERSION` bumps when our cookie policy materially changes
 *     (e.g. we add a new sub-processor or a new tracking SDK). Bumping
 *     the version invalidates any stored consent and shows the banner
 *     again. Audit trail: the recorded consent ALWAYS carries the
 *     version it was recorded against.
 */

/**
 * Bump on any material change to the cookie policy. The banner re-prompts
 * any visitor whose stored consent is for an older version, and the audit
 * trail (server-side) keeps every recorded consent envelope with its
 * version stamp so we can prove which exact policy a visitor agreed to.
 */
export const POLICY_VERSION = '2026-05-17';

/** Cookie category — `necessary` is implicit (always true) but kept in
 *  the type so the persisted shape is symmetrical. */
export type CookieCategory = 'necessary' | 'analytics' | 'marketing';

export interface ConsentChoices {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export interface StoredConsent {
  /** Snapshot of the user's choices at decision-time. */
  choices: ConsentChoices;
  /** Policy version this consent was recorded against. */
  version: string;
  /** ISO timestamp of the decision (local browser clock). */
  decidedAt: string;
  /** Whether the decision was forced by DNT (we never silently flip
   *  analytics/marketing on if DNT is honoured — but we DO record the
   *  decision so the server has an audit trail). */
  viaDnt: boolean;
}

const STORAGE_KEY = 'elreign.cookieConsent.v1';

/**
 * Wraps localStorage so unit tests and SSR call sites don't blow up. We
 * intentionally swallow exceptions: a failed read should never block the
 * page from rendering, and a failed write at worst causes the banner to
 * re-show next visit (better than throwing in a render path).
 */
function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the visitor's stored consent. Returns `null` when:
 *   - No decision has been recorded (banner should show)
 *   - The stored decision is for an older POLICY_VERSION (banner re-shows)
 *   - The stored value is malformed (banner re-shows; we'll overwrite)
 */
export function getConsent(): StoredConsent | null {
  const store = safeStorage();
  if (!store) return null;
  const raw = store.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (
      !parsed ||
      typeof parsed.version !== 'string' ||
      typeof parsed.decidedAt !== 'string' ||
      !parsed.choices ||
      typeof parsed.choices !== 'object'
    ) {
      return null;
    }
    if (parsed.version !== POLICY_VERSION) return null;
    // Defensive shape coercion — necessary must always be true.
    const c = parsed.choices;
    return {
      choices: {
        necessary: true,
        analytics: Boolean(c.analytics),
        marketing: Boolean(c.marketing),
      },
      version: parsed.version,
      decidedAt: parsed.decidedAt,
      viaDnt: Boolean(parsed.viaDnt),
    };
  } catch {
    return null;
  }
}

/**
 * Persist a consent decision. Always writes to localStorage. Returns the
 * full envelope (the same value `getConsent()` will return on next read).
 */
export function setConsent(
  choices: Omit<ConsentChoices, 'necessary'>,
  opts: { viaDnt?: boolean; now?: () => Date } = {},
): StoredConsent {
  const envelope: StoredConsent = {
    choices: {
      necessary: true,
      analytics: choices.analytics,
      marketing: choices.marketing,
    },
    version: POLICY_VERSION,
    decidedAt: (opts.now ?? (() => new Date()))().toISOString(),
    viaDnt: Boolean(opts.viaDnt),
  };
  const store = safeStorage();
  if (store) {
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(envelope));
    } catch {
      /* quota / private-browsing — banner will re-show next visit, fine */
    }
  }
  return envelope;
}

/**
 * Whether the banner should be shown to this visitor right now.
 *
 * The contract is "show whenever we don't have a fresh, valid decision
 * for the current policy version". DNT is NOT a reason to suppress the
 * banner — POPIA + GDPR both require an affirmative on-screen opt-in
 * even when DNT is set; we just pre-tick the toggles to "decline" and
 * include a copy line explaining why.
 */
export function shouldShowBanner(stored: StoredConsent | null = getConsent()): boolean {
  return stored === null;
}

/**
 * Whether the browser is sending DNT. Returns false in non-browser
 * environments. Reads `navigator.doNotTrack` (the W3C spec value) and
 * the MS variant — both still exist in the wild.
 *
 * Returns true ONLY when the value is "1" or "yes" — anything else
 * (null, "0", "unspecified") means the visitor hasn't expressed a
 * preference and we should NOT auto-decline on their behalf.
 */
export function respectsDnt(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    doNotTrack?: string;
    msDoNotTrack?: string;
  };
  const dnt =
    nav.doNotTrack ??
    nav.msDoNotTrack ??
    (typeof window !== 'undefined'
      ? (window as Window & { doNotTrack?: string }).doNotTrack
      : undefined);
  return dnt === '1' || dnt === 'yes';
}

/**
 * Compute the default banner toggles for a fresh visit. If DNT is on,
 * analytics + marketing default to OFF (the banner shows with both
 * unticked + a small note explaining DNT was detected). If DNT is off,
 * everything defaults to OFF too — POPIA + GDPR forbid pre-ticked opt-
 * ins. The "Accept all" CTA is what flips them on.
 */
export function defaultChoices(): Omit<ConsentChoices, 'necessary'> {
  return { analytics: false, marketing: false };
}

/**
 * Absolute (or relative, in local dev) URL for the consent endpoint.
 *
 * Same per-env base-URL rule as `lib/api/client.ts`'s `api()` and the
 * identical `basePath()` fix in `lib/api/external-signing.ts` (2026-07-19
 * P0, the emailed external-signing page): the Static Web App does not
 * forward a relative `/api/*` path to APIM in southafricanorth, so a bare
 * relative fetch here 405s in every DEPLOYED environment (dev/staging/prod)
 * and only ever worked under the local Vite dev proxy — meaning the consent
 * decision was never actually recorded server-side outside local dev
 * (e2e-20260821 finding). Read per-call (not module-level) so tests can
 * stub `VITE_API_BASE_URL`.
 */
export function consentEndpoint(): string {
  const base = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';
  return `${base}/api/v1/me/consent`;
}

/**
 * POST the consent decision to the backend so it ends up on the
 * authenticated user's record (and the audit trail). Fire-and-forget:
 * a failed POST should not block the banner from dismissing — the
 * localStorage write is the source of truth client-side and the
 * server-side write will be retried on the user's next authenticated
 * page-load via re-emit (out of scope here).
 *
 * Returns true on a 2xx; false on anything else (network error, 401,
 * 5xx). Callers can choose to surface a toast or just log telemetry.
 */
export async function postConsentToBackend(
  envelope: StoredConsent,
  accessToken: string | null,
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  if (!accessToken) return false;
  try {
    const res = await fetcher(consentEndpoint(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        version: envelope.version,
        decidedAt: envelope.decidedAt,
        analytics: envelope.choices.analytics,
        marketing: envelope.choices.marketing,
        viaDnt: envelope.viaDnt,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
